import axiosInstance from "./axios";
import { ResponseType } from "@/types/CommonTypes";
import {
  ActorConceptsTotalResponse,
  CreateConceptCommentPayload,
  ActorConceptConfigResponse,
  UpdateActorConceptPayload,
  UpdateActorConceptFieldsPayload,
  ActorConceptCommentResponse,
} from "@/types/ActorConceptTypes";
import { ActorType } from "@/types/ActorTypes";
import { ScenarioType } from "@/types/ScenarioTypes";

export const actorConceptsApi = {
  getTotal: async (
    code: string
  ): Promise<ResponseType<ActorConceptsTotalResponse>> => {
    try {
      const response = await axiosInstance.get(
        `/actor-concepts/total/${code}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch actor concepts total:", error);
      throw error;
    }
  },

  createComment: async (
    payload: CreateConceptCommentPayload
  ): Promise<ResponseType<{ id: number }>> => {
    try {
      const response = await axiosInstance.post(
        `/actor-concept-comments`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Failed to create concept comment:", error);
      throw error;
    }
  },

  getConfig: async (
    code: string,
    version: number
  ): Promise<ResponseType<ActorConceptConfigResponse>> => {
    try {
      const response = await axiosInstance.get(
        `/actor-concepts/config/${code}/${version}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch actor concept config:", error);
      throw error;
    }
  },

  update: async (
    id: number,
    payload: UpdateActorConceptPayload
  ): Promise<ResponseType<ActorConceptConfigResponse>> => {
    try {
      const response = await axiosInstance.put(
        `/actor-concepts/${id}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update actor concept:", error);
      throw error;
    }
  },

  getCommentById: async (
    id: number
  ): Promise<ResponseType<ActorConceptCommentResponse>> => {
    try {
      const response = await axiosInstance.get(`/actor-concept-comments/${id}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch actor concept comment:", error);
      throw error;
    }
  },

  updatePrompt: async (
    id: number,
    prompt: string
  ): Promise<ResponseType<ActorConceptConfigResponse>> => {
    try {
      const response = await axiosInstance.patch(
        `/actor-concepts/${id}/prompt`,
        { prompt }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update actor concept prompt:", error);
      throw error;
    }
  },

  updateActorConceptFields: async (
    id: number,
    payload: UpdateActorConceptFieldsPayload
  ): Promise<ResponseType<ActorConceptConfigResponse>> => {
    try {
      const response = await axiosInstance.patch(
        `/actor-concepts/${id}/actor-concept-fields`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update actor concept fields:", error);
      throw error;
    }
  },

  saveNewVersion: async (
    code: string,
    version: number,
    payload?: { prompt?: string; notes?: string } & UpdateActorConceptFieldsPayload
  ): Promise<ResponseType<ActorConceptConfigResponse>> => {
    try {
      const body: Record<string, unknown> = { code, version };
      if (payload) {
        if (payload.prompt !== undefined) body.prompt = payload.prompt;
        if (payload.notes !== undefined) body.notes = payload.notes;
        if (payload.intro_prompt !== undefined) body.intro_prompt = payload.intro_prompt;
        if (payload.recurrent_instructions !== undefined) body.recurrent_instructions = payload.recurrent_instructions;
        if (payload.max_call_duration !== undefined) body.max_call_duration = payload.max_call_duration;
        if (payload.minimum_interaction !== undefined) body.minimum_interaction = payload.minimum_interaction;
        if (payload.additional_info !== undefined) body.additional_info = payload.additional_info;
      }
      const response = await axiosInstance.post(
        `/actor-concepts/save-new-version`,
        body
      );
      return response.data;
    } catch (error) {
      console.error("Failed to save new actor concept version:", error);
      throw error;
    }
  },

  publish: async (
    code: string,
    version: number
  ): Promise<ResponseType<{ actor_concept: ActorConceptConfigResponse; actor: unknown }>> => {
    try {
      const response = await axiosInstance.post(
        `/actor-concepts/publish`,
        { code, version }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to publish actor concept:", error);
      throw error;
    }
  },

  manuallyCreateActor: async (
    scenarioName?: string,
    scenarioDescription?: string,
    selectedGroupId?: string | null,
  ): Promise<
    ResponseType<{
      actor_concept: ActorConceptConfigResponse;
      actor: ActorType;
      scenario: ScenarioType | null;
    }>
  > => {
    try {
      const response = await axiosInstance.post(
        `/actor-concepts/manually-create`,
        {
          ...(scenarioName && { scenario_name: scenarioName }),
          ...(scenarioDescription && {
            scenario_description: scenarioDescription,
          }),
          ...(selectedGroupId && { selectedGroupId: selectedGroupId }),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to manually create actor:", error);
      throw error;
    }
  },
};
